/**
 * meee-perf.js v1.0
 * MeeeShop Performance Utility Script
 * 
 * Purpose: Self-contained performance enhancements that survive theme updates.
 * Loaded deferred at bottom of <body> — zero impact on FCP or LCP.
 * 
 * Features:
 *  1. Lazy image intersection observer (fallback for browsers without native lazy-load)
 *  2. Passive event listeners for scroll performance
 *  3. Third-party script interaction delay (load heavy scripts only after first user gesture)
 *  4. Image decode optimization (async decode for offscreen images)
 *  5. Connection prewarming for likely next navigations
 */

(function () {
  'use strict';

  // ===== 1. NATIVE LAZY LOAD POLYFILL =====
  // For browsers that support IntersectionObserver but not native lazy loading
  if ('IntersectionObserver' in window && !('loading' in HTMLImageElement.prototype)) {
    const imgObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          imgObserver.unobserve(img);
        }
      });
    }, { rootMargin: '200px 0px' });

    document.querySelectorAll('img[data-src], img[data-srcset]').forEach(function (img) {
      imgObserver.observe(img);
    });
  }

  // ===== 2. ASYNC IMAGE DECODE FOR OFFSCREEN IMAGES =====
  // Prevents decode from blocking main thread for below-fold images
  if ('decode' in HTMLImageElement.prototype) {
    document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.decoding = 'async';
    });
  }

  // ===== 3. INTERACTION-DEFERRED THIRD-PARTY SCRIPTS =====
  // Load heavy analytics/tracking scripts only after first user gesture.
  // This reduces TBT (Total Blocking Time) significantly on mobile.
  // Scripts defined in content_for_header cannot be controlled, but we
  // can ensure our own non-critical scripts load post-interaction.
  const DEFER_EVENTS = ['mouseover', 'touchstart', 'keydown', 'scroll'];
  let thirdPartyLoaded = false;

  function loadDeferredScripts() {
    if (thirdPartyLoaded) return;
    thirdPartyLoaded = true;

    // Remove event listeners after first interaction
    DEFER_EVENTS.forEach(function (evt) {
      document.removeEventListener(evt, loadDeferredScripts, { passive: true });
    });

    // Any future non-critical scripts can be loaded here:
    // Example: load chat widget, reviews widget, etc.
    // document.dispatchEvent(new Event('meee:deferred-ready'));
  }

  // Give 3 seconds for page to settle, then load regardless
  var deferTimer = setTimeout(loadDeferredScripts, 3000);

  DEFER_EVENTS.forEach(function (evt) {
    document.addEventListener(evt, loadDeferredScripts, { passive: true, once: true });
  });

  // ===== 4. PASSIVE SCROLL / TOUCH LISTENERS =====
  // Ensure scroll-based JS never blocks the main thread
  // (Chrome enforces this, but this ensures older browsers comply too)
  try {
    var opts = Object.defineProperty({}, 'passive', {
      get: function () {
        window._passiveSupported = true;
        return false;
      }
    });
    window.addEventListener('test', null, opts);
    window.removeEventListener('test', null, opts);
  } catch (e) {
    window._passiveSupported = false;
  }

  // ===== 5. PREWARM CONNECTIONS FOR HOVERED LINKS =====
  // When a user hovers over an internal product/collection link,
  // prewarm the DNS + TCP connection so navigation starts faster.
  var prewarmedUrls = new Set();

  document.addEventListener('mouseover', function (e) {
    var anchor = e.target.closest('a[href]');
    if (!anchor) return;

    var href = anchor.href;
    // Only prewarm internal links that aren't already cached
    if (href && href.startsWith(window.location.origin) && !prewarmedUrls.has(href)) {
      // Avoid prewarming cart/checkout/account
      if (/\/(cart|checkout|account)/.test(href)) return;

      prewarmedUrls.add(href);

      // Create a preconnect link to prewarm TCP/TLS
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      link.as = 'document';
      document.head.appendChild(link);
    }
  }, { passive: true });

  // ===== 6. REDUCE CLS FROM LATE-LOADING IMAGES =====
  // Find all images without explicit width/height and add aspect-ratio
  // to prevent layout shift when they load.
  document.querySelectorAll('img:not([width]):not([height])').forEach(function (img) {
    if (img.naturalWidth && img.naturalHeight) {
      img.style.aspectRatio = img.naturalWidth + ' / ' + img.naturalHeight;
    } else {
      img.addEventListener('load', function () {
        if (img.naturalWidth && img.naturalHeight) {
          img.style.aspectRatio = img.naturalWidth + ' / ' + img.naturalHeight;
        }
      }, { once: true });
    }
  });

})();
